import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import ReadMore from '../../_molecules/ReadMore';
import LinkedInIcon from '@icons/LinkedInIcon';
import TwitterIcon from '@icons/TwitterIcon';
import Button from '@atoms/Button';
import MemberEmail from '@atoms/MemberEmail';
import AddressBlock from '@molecules/AddressBlock';
import { DivWithShadowAlternate, LabelLarge, LinkSmall } from '@styled';

// Functionality
import { mediaQueries } from '@general/breakpoints';
import { getMemberDesignations, getMemberFullName } from '@general/entities/member';
import fonts from '@general/fonts';
import { transformLocation } from '@general/location';
import useMemberFull from '@hooks/useMemberFull';
import { useSendMessageModal } from '@molecules/SendMessageModal';
import useIcscTracking from '@tracking/useIcscTracking';

export const profileDetailTypes = {
    ROW: 'row',
    CTA: 'cta',
    BREAK: 'break',
};

const ContainerStyled = styled(DivWithShadowAlternate)`
    height: 100%;
    padding: 60px;
`;

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;

    span,
    a {
        line-height: 18px;
    }
`;

const Label = styled(LabelLarge)`
    width: 80px;
`;

const BodySmall = styled.div`
    font-family: ${fonts.sans};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    margin: 0;
`;

const ContainerInnerStyled = styled.div`
    display: flex;
    flex-direction: column;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, 1fr);
    grid-column-gap: 0;
    grid-row-gap: 30px;
    grid-column-gap: 50px;

    @media ${mediaQueries.md} {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: 1fr;
    }
    @media ${mediaQueries.lg} {
        display: flex;
        flex-direction: column;
    }
    @media ${mediaQueries.xl} {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: 1fr;
    }
`;

const ProfileDetailsCard = ({ graphID, isFavorite }) => {
    const { data } = useMemberFull(graphID);
    const { openModal } = useSendMessageModal();
    const { trackEvent } = useIcscTracking();

    const handleOnClick = () => {
        openModal({
            id: data.recordId,
            graphID: data.id,
            companyID: data.company?.recordId,
            companyName: data.company?.name,
            displayLocation: transformLocation(data.address),
            fullName: getMemberFullName(data.firstName, data.lastName, data.suffix),
            firstName: data.firstName,
            lastName: data.lastName,
            designations: data.designations.join(', '),
            businessType: data.businessType,
            title: data.title,
            photoURL: data.photoUrl,
            isFavorite,
            showMemberDetailsCTA: false,
            onMessage: () => {
                trackEvent({
                    type: 'click',
                    target: 'send_a_message_modal_submit_button',
                    value: `Messaged ${data.firstName} ${data.lastName}`,
                });
            },
        });
    };

    if (!data) {
        return null;
    }

    return (
        <ContainerStyled>
            <ContainerInnerStyled>
                <div>
                    {data.workPhone && (
                        <Row className="mb-1rem">
                            <Label>Phone:</Label>
                            <LinkSmall href={`tel:${data.workPhone}`}>{data.workPhone}</LinkSmall>
                        </Row>
                    )}

                    <Row className="mb-1rem">
                        <Label>Email:</Label>
                        {data.displayEmail && <MemberEmail id={data.recordId} />}
                        {!data.displayEmail && <BodySmall>Email not available</BodySmall>}
                    </Row>

                    {data.blogUrl && (
                        <Row className="mb-1rem">
                            <Label>Website:</Label>
                            <LinkSmall href={data.blogUrl} target="_blank">
                                {data.blogUrl}
                            </LinkSmall>
                        </Row>
                    )}
                    {data.address && (
                        <Row className="mb-1rem">
                            <Label>Address:</Label>
                            <BodySmall>
                                <AddressBlock address={data.address} />
                            </BodySmall>
                        </Row>
                    )}
                    {(data.linkedinUrl || data.twitterUrl) && (
                        <Row className="mb-1rem">
                            <Label>Social:</Label>
                            <div className="d-flex">
                                {data.linkedinUrl && (
                                    <a className="mr-1.5rem" href={data.linkedinUrl} target="_blank" rel="noreferrer">
                                        <LinkedInIcon />
                                    </a>
                                )}

                                {data.twitterUrl && (
                                    <a className="mr-1.5rem" href={data.twitterUrl} target="_blank" rel="noreferrer">
                                        <TwitterIcon />
                                    </a>
                                )}
                            </div>
                        </Row>
                    )}
                    <Row>
                        <Button onClick={handleOnClick}>Send a Message</Button>
                    </Row>
                </div>
                <div>
                    {data.firstCollege && (
                        <Row className="mb-1rem">
                            <Label>Education:</Label>
                            <BodySmall>
                                {data.firstCollege.name}
                                {data.firstGraduationYear && `, ${data.firstGraduationYear}`}
                                {data.secondCollege && (
                                    <>
                                        <br />
                                        {data.secondCollege.name}
                                        {data.secondGraduationYear && `, ${data.secondGraduationYear}`}
                                    </>
                                )}
                            </BodySmall>
                        </Row>
                    )}
                    {data.description && (
                        <Row className="mb-1rem">
                            <Label>About:</Label>
                            <BodySmall>
                                <ReadMore>{data.description}</ReadMore>
                            </BodySmall>
                        </Row>
                    )}
                </div>
            </ContainerInnerStyled>
        </ContainerStyled>
    );
};

ProfileDetailsCard.propTypes = {
    graphID: PropTypes.string,
    isFavorite: PropTypes.bool,
};

export default ProfileDetailsCard;
